import { FEATURES } from '@constants/Text'
import { deveVerSoFeeFixo } from '@utils/escritorioFeeFixoMigracao'
import { isHomeIaEnabled } from '@modules/aawz-intelligence-v2/utils/home-ia-enabled'
import { isNav2026Enabled } from '@modules/navegacao-v2/utils/nav2026-enabled'
import {
  ROTA_IMERSIVA,
  ROTA_IMERSIVA_CONVERSA,
} from '@modules/aawz-intelligence-v2/views/chat-imersivo/constants'

/**
 * Fonte ÚNICA da regra "o atalho flutuante do chat de IA aparece?".
 *
 * Antes a regra era escrita duas vezes — `showChatIaWidget` (router/layouts/main.vue)
 * e `showPopUp` (global/components/chat-ia-pop-up) — e as duas cópias já tinham
 * divergido: `aawz-intelligence.alteracoes-ia` só constava na do popup. O layout
 * então achava que havia widget para recolher onde não havia, e renderizava o
 * handle do dock sozinho. É o mesmo "handle órfão" que o comentário do
 * `showChatIaWidget` registra (12/08/2026) por outro caminho — sinal de que a
 * duplicação, e não cada esquecimento, era o defeito.
 *
 * Quem acrescenta uma superfície de chat acrescenta a rota AQUI, uma vez.
 */

// Rotas onde o chat já É a tela: um FAB para o mesmo chat seria uma segunda porta
// para a MESMA conversa (todas compartilham o namespace `aawz-intelligence-v2`), e
// duas instâncias do motor no mesmo store disputam `chatLoading`/`responseTokens`
// e o `abortController` único do `apiChat`.
export const ROTAS_SEM_WIDGET_CHAT_IA = [
  'aawz-intelligence',
  'aawz-intelligence.chat-ia',
  'aawz-intelligence.alteracoes-ia',
  // Visão imersiva (a home por IA, `/home-ia`): aqui o chat ocupa a janela
  // inteira e não há plataforma em volta para um atalho flutuar por cima.
  ROTA_IMERSIVA,
  // ★ A MESMA tela, com a conversa no endereço (`/home-ia/c/<uuid>`). Esta
  // lista casa por IGUALDADE EXATA de nome de rota, então a rota-mãe não
  // cobre a filha: sem esta linha o FAB voltava a flutuar sobre a tela cheia
  // no instante em que a conversa ganha id — o "handle órfão" que o
  // comentário acima registra. O teste que cobra isto já existia
  // (`routes.conversa-com-url.unit.js`) e estava vermelho desde `c55a7077`.
  ROTA_IMERSIVA_CONVERSA,
]

/**
 * Este USUÁRIO pode usar o chat de IA em qualquer superfície? Regra sem rota —
 * é o gate que o item de menu, o atalho de teclado e o widget compartilham.
 * Separado porque um atalho que navega sem checar isto vira oráculo: o
 * `customPermission` da rota barra e o usuário descobre a funcionalidade
 * caindo num /404.
 *
 * @param {Object} opts
 * @param {Object} opts.user usuário atual (`$currentUser()`)
 * @param {Object} opts.access mapa de features (`$access`)
 * @returns {boolean}
 */
export function podeUsarChatIa({ user, access } = {}) {
  const usuario = user || {}
  if (usuario.IS_CLIENTE) return false
  // Escritório Fee Fixo Migração, não-gestor: só usa o módulo Fee Fixo, e o
  // chat de IA não faz parte dessa experiência (achado do usuário, 12/08/2026).
  if (deveVerSoFeeFixo(usuario)) return false
  return !!(access || {})[FEATURES.CHAT_IA]
}


/**
 * Este usuário vê a IA Driven em tela cheia (`/home-ia`)?
 *
 * Estreita `podeUsarChatIa` e lê a flag PRÓPRIA da feature (`home_ia`) pelo
 * MESMO `isHomeIaEnabled` do guard — flag real OU override de dev —, senão o
 * override redireciona a raiz para uma rota que este gate recusa (/404 na
 * raiz). Ter `AAWZ Intelligence` não basta: cada escritório precisa da linha
 * `home_ia` em `escritorio_funcionalidades` (operação de DADO, feita no
 * rollout — a migration v632 só libera o nome no CHECK).
 *
 * É a régua ÚNICA dos cinco caminhos de entrada:
 *
 *   1. a ROTA — `checkImersivaPermission` em `aawz-intelligence/routes.js`;
 *   2. o ITEM DE MENU — `validShow` em `constants/navigation-routes.js`
 *      (o `flag` de `ROUTE_DEFS` é `null`: ninguém o lia);
 *   3. o ATALHO Cmd/Ctrl+I — `atalho-ia-imersiva.vue`;
 *   4. o GUARD da raiz — `applyHomeIaRedirectGuard` em `router/index.js` chama
 *      o próprio `checkImersivaPermission` do destino;
 *   5. a BUSCA GLOBAL — de graça: `search-index.js` espelha o
 *      `customPermission` da rota, então o item some do Ctrl+K junto com (1).
 *
 * Nenhum deles pode ler a flag por conta própria: uma régua paralela (flag
 * crua de um lado, override do outro) manda o usuário para uma rota que a
 * outra recusa, e ele cai em /404 — o "vira oráculo" de `podeUsarChatIa`.
 *
 * @param {Object} opts
 * @param {Object} opts.user usuário atual (`$currentUser()`)
 * @param {Object} opts.access mapa de features (`$access` / `show`)
 * @returns {boolean}
 */
export function podeVerIaDriven({ user, access } = {}) {
  if (!podeUsarChatIa({ user, access })) return false
  // Só na nav 2026: a v1 renderiza `meta.imersivo` sem `<v-app>` e sem alvo
  // para o "Voltar para Gestão" (main.vue) — até ela ter suporte, fica fechada.
  if (!isNav2026Enabled(access)) return false
  return isHomeIaEnabled(access)
}

/**
 * @param {Object} opts
 * @param {string} opts.routeName nome da rota atual (`$route.name`)
 * @param {Object} opts.user usuário atual (`$currentUser()`)
 * @param {Object} opts.access mapa de features (`$access`)
 * @returns {boolean}
 */
export function deveMostrarChatIaWidget({ routeName, user, access } = {}) {
  if (!podeUsarChatIa({ user, access })) return false
  return ROTAS_SEM_WIDGET_CHAT_IA.indexOf(routeName) === -1
}
